import { useEffect, useState, useRef, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

// Lusano-style cinematic easing - slow start, smooth middle, gentle end
const lusanoEase = [0.76, 0, 0.24, 1] as const;

// Transition context to coordinate content fade
const TransitionContext = createContext<{ isTransitioning: boolean }>({ isTransitioning: false });
export const usePageTransition = () => useContext(TransitionContext);

export const PageTransition = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"idle" | "exit" | "enter">("idle");
  const pendingPath = useRef<string | null>(null);
  const isFirstRender = useRef(true);

  // Handle the enter animation after navigation completes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // If we just navigated (pendingPath was set), start enter phase
    if (pendingPath.current && location.pathname === pendingPath.current) {
      pendingPath.current = null;
      // Scroll to top immediately
      window.scrollTo(0, 0);
      // Start enter animation
      setPhase("enter");
      // Return to idle after animation completes
      setTimeout(() => {
        setPhase("idle");
      }, 800);
    }
  }, [location.pathname]);

  // Intercept link clicks to show overlay first
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link || !link.href) return;
      
      // Skip external links, hash links, and links with target
      if (link.target || link.href.includes('#')) return;
      
      try {
        const url = new URL(link.href);
        
        // Only handle internal navigation to different paths
        if (url.origin === window.location.origin && url.pathname !== location.pathname) {
          e.preventDefault();
          e.stopPropagation();
          
          // Start exit animation
          setPhase("exit");
          pendingPath.current = url.pathname + url.search;
          
          // Navigate after overlay covers screen
          setTimeout(() => {
            navigate(pendingPath.current!);
          }, 600);
        }
      } catch {
        // Invalid URL, let default behavior happen
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [location.pathname, navigate]);

  return (
    <TransitionContext.Provider value={{ isTransitioning: phase !== "idle" }}>
      {/* Content wrapper with fade */}
      <motion.div
        animate={{
          opacity: phase === "exit" ? 0 : 1,
          scale: phase === "exit" ? 0.98 : 1,
          y: phase === "enter" ? [20, 0] : 0,
        }}
        transition={{
          duration: phase === "exit" ? 0.4 : 0.6,
          ease: lusanoEase,
        }}
        style={{ transformOrigin: "center center" }}
      >
        {children}
      </motion.div>
      
      {/* Overlay - warm charcoal like Lusano */}
      <AnimatePresence>
        {phase !== "idle" && (
          <motion.div
            className="fixed inset-0"
            style={{
              zIndex: 99999,
              backgroundColor: "#000",
              pointerEvents: "all",
            }}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: phase === "exit" ? 1 : 0,
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: phase === "exit" ? 0.5 : 0.7,
              ease: lusanoEase,
            }}
          />
        )}
      </AnimatePresence>
    </TransitionContext.Provider>
  );
};
